import type { UpdateTool } from '@/types/tool.types';

/**
 * The column model of the tools table on `/assemblies`.
 *
 * Lifted out of the route: the table now offers twenty-two columns, four presets and a saved
 * visibility set, and none of that is rendering — it is the description of what *can* be
 * rendered, which both the table and the column chooser read.
 *
 * Everything a column knows about itself lives in one `ColumnDef`: which field it reads, how it
 * edits, which group it belongs to in the chooser, and how wide it starts. The cell renderer
 * branches on `kind` rather than on field names, so adding a thirteenth measurement column is an
 * entry in `COLUMNS` and two locale keys, not another `if`.
 */

/**
 * Columns filled from the tool's assemblies rather than from the tool row.
 *
 * Since the tool-database redesign the holder and the magazine slot belong to the
 * assembly, so these two are read-only: there is no single tool field to write back to.
 * Named in camelCase to keep them apart from the snake_case API-backed column keys.
 */
export const DERIVED_COLUMN_KEYS = ['holders', 'ncNumbers'] as const;

export type DerivedColumnKey = (typeof DERIVED_COLUMN_KEYS)[number];

/**
 * Columns read straight off `ToolModel` that `UpdateTool` has no field for.
 *
 * `updated_at` is the server's own bookkeeping: it is worth showing and it is not ours to
 * write, so it sits outside the editable key union rather than being faked into it.
 */
export const READ_ONLY_COLUMN_KEYS = ['updated_at'] as const;

export type ReadOnlyColumnKey = (typeof READ_ONLY_COLUMN_KEYS)[number];

export type ColumnKey = keyof UpdateTool | DerivedColumnKey | ReadOnlyColumnKey;

export function isDerivedColumn(key: ColumnKey): key is DerivedColumnKey {
  return (DERIVED_COLUMN_KEYS as readonly string[]).includes(key);
}

/**
 * Whether the key names a column the update payload has no field for.
 *
 * A type predicate rather than a boolean, because the cell renderer uses it to narrow
 * `ColumnKey` back down to `keyof UpdateTool` for everything below the read-only branches.
 */
export function isReadOnlyColumn(key: ColumnKey): key is ReadOnlyColumnKey {
  return (READ_ONLY_COLUMN_KEYS as readonly string[]).includes(key);
}

/** How a column's values read: words to the left, measurements centred under their heading. */
export type ColumnAlign = 'left' | 'right' | 'center';

/**
 * What a cell of this column is made of — the one thing the renderer branches on.
 *
 * `text` and `number` edit in place through PrimeReact inputs, `choice` and `boolean` through
 * the shared dropdown editor, `date` and `derived` do not edit at all.
 */
export type ColumnKind = 'text' | 'number' | 'choice' | 'boolean' | 'date' | 'derived';

/** Which block of the column chooser a column is listed under. */
export type ColumnGroup = 'identity' | 'assembly' | 'dimensions' | 'geometry';

export const COLUMN_GROUPS: readonly ColumnGroup[] = ['identity', 'assembly', 'dimensions', 'geometry'];

export const COLUMN_GROUP_LABEL_KEYS: Record<ColumnGroup, string> = {
  identity: 'toolManagement.columnsGroupIdentity',
  assembly: 'toolManagement.columnsGroupAssembly',
  dimensions: 'toolManagement.columnsGroupDimensions',
  geometry: 'toolManagement.columnsGroupGeometry',
};

export interface ColumnDef {
  key: ColumnKey;
  labelKey: string;
  kind: ColumnKind;
  group: ColumnGroup;
  align: ColumnAlign;
  /**
   * Unit written into the header, so the cells can carry bare numbers.
   *
   * Deliberately not a translation key: `mm` and `°` are the same symbol in every language the
   * app speaks, and repeating them in each cell is what made the old table hard to scan.
   */
  unit?: 'mm' | '°';
  /** In the table before the user has ever opened the column chooser. */
  defaultVisible: boolean;
  /** Whether a double-click on a cell of this column opens an editor. */
  editable: boolean;
  /** The backing field accepts `null` — an empty cell is a value, not missing data. */
  nullable?: boolean;
  /**
   * Starting width in px.
   *
   * The measurement columns are sized to their longest *header*, not their values: the numbers
   * are two or three digits, but "Celková délka (mm)" / "Overall length (mm)" is what decides
   * whether the header reads or truncates in either language. The optional columns were measured
   * the same way, in both locales — which is why "Vodorovná vzdálenost (mm)" is 232 px wide for
   * three digits of value. Centring splits that slack evenly, so two digits still sit under the
   * middle of their heading instead of drifting to one edge of a column sized by its label.
   */
  defaultWidth: number;
}

/**
 * Every column the table can show, in the order it shows them by default.
 *
 * Nine are `defaultVisible`: what identifies a tool, what it is held by, and the three
 * measurements every tool has. The other thirteen are a chooser click away.
 */
export const COLUMNS: ColumnDef[] = [
  {
    key: 'tool_name',
    labelKey: 'toolManagement.toolName',
    kind: 'text',
    group: 'identity',
    align: 'left',
    defaultVisible: true,
    editable: true,
    defaultWidth: 268,
  },
  {
    key: 'tool_type',
    labelKey: 'toolManagement.toolType',
    kind: 'choice',
    group: 'identity',
    align: 'left',
    defaultVisible: true,
    editable: true,
    defaultWidth: 152,
  },
  {
    key: 'purpose',
    labelKey: 'toolManagement.purpose',
    kind: 'choice',
    group: 'identity',
    align: 'left',
    defaultVisible: true,
    editable: true,
    defaultWidth: 112,
  },
  {
    key: 'holders',
    labelKey: 'toolManagement.holderName',
    kind: 'derived',
    group: 'assembly',
    align: 'left',
    defaultVisible: true,
    editable: false,
    defaultWidth: 150,
  },
  {
    key: 'ncNumbers',
    labelKey: 'toolManagement.ncNumber',
    kind: 'derived',
    group: 'assembly',
    align: 'left',
    defaultVisible: true,
    editable: false,
    defaultWidth: 120,
  },
  {
    key: 'tool_diameter',
    labelKey: 'toolManagement.colDiameter',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: true,
    editable: true,
    defaultWidth: 136,
  },
  {
    key: 'cutting_diameter',
    labelKey: 'toolManagement.colCuttingDiameter',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 196,
  },
  {
    key: 'tool_overall_len',
    labelKey: 'toolManagement.colOverallLen',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: true,
    editable: true,
    defaultWidth: 160,
  },
  {
    key: 'tool_len',
    labelKey: 'toolManagement.colLen',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: true,
    editable: true,
    defaultWidth: 120,
  },
  {
    key: 'tool_cut_len',
    labelKey: 'toolManagement.colCutLen',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 148,
  },
  {
    key: 'thread_length',
    labelKey: 'toolManagement.colThreadLength',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 172,
  },
  {
    key: 'tool_corner_rad',
    labelKey: 'toolManagement.colCornerRad',
    kind: 'number',
    group: 'dimensions',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 172,
  },
  {
    key: 'tool_cut_angle',
    labelKey: 'toolManagement.colCutAngle',
    kind: 'number',
    group: 'geometry',
    align: 'center',
    unit: '°',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 124,
  },
  {
    key: 'tip_angle',
    labelKey: 'toolManagement.colTipAngle',
    kind: 'number',
    group: 'geometry',
    align: 'center',
    unit: '°',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 136,
  },
  {
    key: 'flank_angle',
    labelKey: 'toolManagement.colFlankAngle',
    kind: 'number',
    group: 'geometry',
    align: 'center',
    unit: '°',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 136,
  },
  {
    key: 'horizontal_distance',
    labelKey: 'toolManagement.colHorizontalDistance',
    kind: 'number',
    group: 'geometry',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 232,
  },
  {
    key: 'vertical_distance',
    labelKey: 'toolManagement.colVerticalDistance',
    kind: 'number',
    group: 'geometry',
    align: 'center',
    unit: 'mm',
    defaultVisible: false,
    editable: true,
    nullable: true,
    defaultWidth: 196,
  },
  {
    key: 'ball_type',
    labelKey: 'toolManagement.colBallType',
    kind: 'boolean',
    group: 'geometry',
    align: 'left',
    defaultVisible: false,
    editable: true,
    defaultWidth: 144,
  },
  {
    // Off by default: free text is the one column whose width never suits the rest of the
    // table, and the nine that remain are what a tool is identified and measured by. It is
    // one click away in the chooser, and in the "all" preset.
    key: 'tool_comment',
    labelKey: 'toolManagement.toolComment',
    kind: 'text',
    group: 'identity',
    align: 'left',
    defaultVisible: false,
    editable: true,
    defaultWidth: 200,
  },
  {
    key: 'tool_rotation',
    labelKey: 'toolManagement.toolRotation',
    kind: 'text',
    group: 'identity',
    align: 'left',
    defaultVisible: true,
    editable: true,
    defaultWidth: 92,
  },
  {
    // Read-only on purpose: retiring a tool is a decision, and a double-click on a table cell
    // is not where a decision like that should be able to happen by accident.
    key: 'active',
    labelKey: 'toolManagement.colActive',
    kind: 'boolean',
    group: 'identity',
    align: 'left',
    defaultVisible: false,
    editable: false,
    defaultWidth: 112,
  },
  {
    key: 'updated_at',
    labelKey: 'toolManagement.colUpdatedAt',
    kind: 'date',
    group: 'identity',
    align: 'left',
    defaultVisible: false,
    editable: false,
    defaultWidth: 152,
  },
];

/** Pinned to the left edge, so it is never dragged out of first place or switched off. */
export const PINNED_COLUMN: ColumnKey = 'tool_name';

/** Fixed width of the pinned delete column: one 28 px icon button plus its gutter. */
export const ACTIONS_COL_WIDTH = 52;

/**
 * The two pinned columns below `md`, where the whole viewport is 400 px wide.
 *
 * 168 px is the thumbnail (30) plus its gap (8) plus the cell's own padding (24) plus room for a
 * truncated name; 44 px is the delete button plus its gutter. Together they take 212 px of a
 * 400 px screen, which leaves a middle column visible beside them — the point of pinning at all.
 */
export const PHONE_PINNED_COL_WIDTH = 168;
export const PHONE_ACTIONS_COL_WIDTH = 44;

/** Nothing narrower than this stays readable, and the resize handle needs somewhere to sit. */
export const MIN_COL_WIDTH = 56;

/**
 * Suffixed keys: a saved layout is left behind rather than migrated whenever the defaults it
 * was measured against stop being true.
 *
 * `.v2` left `.v1` behind because the name column gained a thumbnail and the column order
 * changed. `.v3` now leaves `.v2` behind because the measurement columns were widened so
 * their headers stop truncating to "D.. (mm)" — a width saved against the old narrow
 * defaults would put the truncation straight back. Older keys stay in storage, unread.
 *
 * The twelve optional columns did *not* earn a `.v4`: every `.v3` key still means the same
 * thing at the same width, and the loaders below tolerate a saved layout that simply does not
 * mention the new ones.
 *
 * The visibility key is versioned separately, and `.v2` leaves `.v1` behind because the
 * default set lost the comment column: a `.v1` entry was written the moment anyone opened the
 * chooser, and it would hold the old ten in place for exactly the users who never asked for
 * them. Same rule as above — the old key stays in storage, unread.
 */
export const LS_COL_WIDTHS_KEY = 'toolManagement.colWidths.v3';
export const LS_COL_ORDER_KEY = 'toolManagement.colOrder.v3';
export const LS_COL_VISIBILITY_KEY = 'toolManagement.colVisibility.v2';

export const DEFAULT_COL_ORDER: ColumnKey[] = COLUMNS.map((c) => c.key);

/** Starting widths, keyed by column. Derived so a new column cannot be added without one. */
export const DEFAULT_COL_WIDTHS: Record<string, number> = Object.fromEntries(
  COLUMNS.map((c) => [c.key, c.defaultWidth])
);

/** The nine columns the table opens with, in `COLUMNS` order. */
export const DEFAULT_VISIBLE_KEYS: ColumnKey[] = COLUMNS.filter((c) => c.defaultVisible).map((c) => c.key);

const KNOWN_COLUMN_KEYS = new Set<string>(DEFAULT_COL_ORDER);

/** A named set of columns the chooser can apply in one click. */
export interface ColumnPreset {
  id: string;
  labelKey: string;
  keys: ColumnKey[];
}

/**
 * The four presets.
 *
 * Each one is a question the user came to the table with — "what does the catalogue say",
 * "how big is it", "what shape is it", "show me everything" — rather than an arbitrary
 * grouping. Every preset keeps the name and the type, because a row of bare numbers with
 * nothing to identify it is not a table.
 */
export const COLUMN_PRESETS: ColumnPreset[] = [
  { id: 'basic', labelKey: 'toolManagement.columnsPresetBasic', keys: DEFAULT_VISIBLE_KEYS },
  {
    id: 'dimensions',
    labelKey: 'toolManagement.columnsPresetDimensions',
    keys: [
      'tool_name',
      'tool_type',
      'tool_diameter',
      'cutting_diameter',
      'tool_overall_len',
      'tool_len',
      'tool_cut_len',
      'thread_length',
      'tool_corner_rad',
    ],
  },
  {
    id: 'geometry',
    labelKey: 'toolManagement.columnsPresetGeometry',
    keys: [
      'tool_name',
      'tool_type',
      'tool_cut_angle',
      'tip_angle',
      'flank_angle',
      'horizontal_distance',
      'vertical_distance',
      'ball_type',
    ],
  },
  { id: 'all', labelKey: 'toolManagement.columnsPresetAll', keys: DEFAULT_COL_ORDER },
];

export function loadColWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(LS_COL_WIDTHS_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Record<string, unknown>;
      const widths = { ...DEFAULT_COL_WIDTHS };
      // Only known columns, and only sane numbers: a stale or hand-edited entry must not
      // be able to collapse a column to nothing.
      for (const key of Object.keys(DEFAULT_COL_WIDTHS)) {
        const value = parsed[key];
        if (typeof value === 'number' && Number.isFinite(value)) {
          widths[key] = Math.max(MIN_COL_WIDTH, Math.round(value));
        }
      }
      return widths;
    }
  } catch {
    // ignore
  }
  return { ...DEFAULT_COL_WIDTHS };
}

/**
 * The stored order, reconciled with the current column set rather than trusted or discarded.
 *
 * The old check demanded the saved array describe exactly today's columns, which meant the very
 * first release that added a column threw away everyone's layout. Instead: keep the known keys
 * in the order they were saved, append whatever the save predates in default order, drop what no
 * longer exists — so a `.v3` order written when there were ten columns still positions those ten
 * and the twelve new ones land behind them.
 */
export function loadColOrder(): ColumnKey[] {
  try {
    const stored = localStorage.getItem(LS_COL_ORDER_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (Array.isArray(parsed)) {
        const seen = new Set<string>();
        const order: ColumnKey[] = [];
        for (const key of parsed) {
          if (typeof key === 'string' && KNOWN_COLUMN_KEYS.has(key) && !seen.has(key)) {
            seen.add(key);
            order.push(key as ColumnKey);
          }
        }
        for (const key of DEFAULT_COL_ORDER) {
          if (!seen.has(key)) order.push(key);
        }
        // An order saved before the column was pinned may put it anywhere; it is hoisted back
        // to the front rather than discarded, so the rest of the saved layout survives.
        return [PINNED_COLUMN, ...order.filter((k) => k !== PINNED_COLUMN)];
      }
    }
  } catch {
    // ignore
  }
  return [...DEFAULT_COL_ORDER];
}

/**
 * The stored visible set, validated: unknown keys dropped, the pinned column always in.
 *
 * An empty or unreadable entry falls back to the default ten rather than to nothing — a table
 * with one column would look broken, and the user would have no way to tell that it is their
 * own saved state doing it.
 */
export function loadColVisibility(): ColumnKey[] {
  try {
    const stored = localStorage.getItem(LS_COL_VISIBILITY_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as unknown;
      if (Array.isArray(parsed)) {
        const keys = parsed.filter((k): k is ColumnKey => typeof k === 'string' && KNOWN_COLUMN_KEYS.has(k));
        if (keys.length > 0) {
          return keys.includes(PINNED_COLUMN) ? keys : [PINNED_COLUMN, ...keys];
        }
      }
    }
  } catch {
    // ignore
  }
  return [...DEFAULT_VISIBLE_KEYS];
}

export function saveColVisibility(keys: ColumnKey[]): void {
  try {
    localStorage.setItem(LS_COL_VISIBILITY_KEY, JSON.stringify(keys));
  } catch {
    // ignore
  }
}

/** Forget every saved table layout, so the next load starts from the defaults above. */
export function clearStoredColumnLayout(): void {
  try {
    localStorage.removeItem(LS_COL_WIDTHS_KEY);
    localStorage.removeItem(LS_COL_ORDER_KEY);
    localStorage.removeItem(LS_COL_VISIBILITY_KEY);
  } catch {
    // ignore
  }
}

/** Whether a visible set is exactly a preset, ignoring the order the keys happen to be in. */
export function matchesPreset(visibleKeys: ColumnKey[], preset: ColumnPreset): boolean {
  if (visibleKeys.length !== preset.keys.length) return false;
  const visible = new Set<string>(visibleKeys);
  return preset.keys.every((key) => visible.has(key));
}
